import React, { useEffect, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { Flashcard, getFlashcardsFromQuestions } from "../services/flashcardService";
import { QuestionFilters, getFilterOptions } from "../services/questionService";
import { canUseFlashcards, incrementFlashcardsUsed } from "../services/flashcardEntitlements";
import { saveExplanation } from "../services/savedExplanationService";

interface FlashcardsPageProps {
  filters?: QuestionFilters;
  onBack: () => void;
  onViewSavedExplanations?: () => void;
}

const FlashcardsPage: React.FC<FlashcardsPageProps> = ({ filters, onBack, onViewSavedExplanations }) => {
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [index, setIndex] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableFilters, setAvailableFilters] = useState<{ subjects: string[]; topics: Record<string, string[]> }>({ subjects: [], topics: {} });
  const [chosenFilters, setChosenFilters] = useState<QuestionFilters>(() => filters || {});
  const [filtersReady, setFiltersReady] = useState<boolean>(false); // sempre passa pela interação de filtros
  const [requestedCount, setRequestedCount] = useState<number>(filters?.limit ? Number(filters.limit) : 20);
  const [limits, setLimits] = useState<{ perGeneration: number; remainingToday: number; dailyLimit: number; isPro: boolean }>({ perGeneration: 10, remainingToday: 0, dailyLimit: 10, isPro: false });
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [stats, setStats] = useState({ seen: 0, hard: 0, medium: 0, easy: 0, ai: 0 });
  const [totalAvailable, setTotalAvailable] = useState<number | null>(null);
  const [aiAnswer, setAiAnswer] = useState<string>("");
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [recentSessions, setRecentSessions] = useState<any[]>([]);
  const [resumeSession, setResumeSession] = useState<any | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [preloaded, setPreloaded] = useState(0);
  const [bookmarked, setBookmarked] = useState<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  const activeFilterLabel = useMemo(() => {
    if (!chosenFilters.subject) return "Selecione a matéria";
    const parts = [] as string[];
    if (chosenFilters.subject) parts.push(chosenFilters.subject);
    if (chosenFilters.topic) parts.push(chosenFilters.topic);
    return parts.join(" • ") || "Selecione a matéria";
  }, [chosenFilters]);

  // Carrega opções de filtro se necessário e preenche seleção se veio pré-populada
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const opts = await getFilterOptions();
        setAvailableFilters({ subjects: opts.subjects || [], topics: opts.topics || {} });
      } catch (err) {
        console.error("Erro ao carregar filtros:", err);
      }
    };
    const loadHistory = async () => {
      try {
        const { getRecentFlashcardSessions } = await import("../services/flashcardSessionService");
        const sessions = await getRecentFlashcardSessions();
        setRecentSessions(sessions || []);
        setResumeSession(sessions?.[0] || null);
      } catch (err) {
        console.error("Erro ao carregar histórico de flashcards:", err);
      }
    };
    loadFilters();
    loadHistory();
  }, []);

  // Se receber filtros pré-populados (plano/dashboard), só pré-seleciona; ainda exige clique em "Gerar"
  useEffect(() => {
    if (filters) {
      setChosenFilters(filters);
      setFiltersReady(false);
    }
  }, [filters]);

  // Carrega limites de uso (free/pro)
  useEffect(() => {
    const loadLimits = async () => {
      const res = await canUseFlashcards();
      setLimits({ perGeneration: res.perGeneration, remainingToday: res.remainingToday, dailyLimit: res.dailyLimit, isPro: res.isPro });
      // ajusta requested para não passar do cap e do restante do dia
      const maxAllowed = Math.max(1, Math.min(res.perGeneration, Math.max(0, res.remainingToday)) || res.perGeneration);
      setRequestedCount((prev) => Math.min(maxAllowed, prev || maxAllowed));
      if (!res.canContinue) {
        setError("Você atingiu o limite diário de flashcards. Faça upgrade para liberar mais.");
      }
    };
    loadLimits();
  }, []);

  // Carrega flashcards quando filtros estão prontos
  useEffect(() => {
    if (!filtersReady) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      setAiAnswer("");
      try {
        const perGen = limits.perGeneration;
        const remaining = Math.max(0, limits.remainingToday);
        if (remaining <= 0) {
          setError("Você atingiu o limite diário de flashcards no plano CONCURSEIRO. Faça upgrade para gerar mais.");
          setLoading(false);
          return;
        }
        const maxRequested = Math.min(perGen, remaining, requestedCount || perGen);
        const bias = computeDifficultyBias();
        const fc = await getFlashcardsFromQuestions({ ...chosenFilters, limit: maxRequested, bias }, maxRequested);
        setCards(fc.slice(0, maxRequested));
        setTotalAvailable(fc.length);
        setPreloaded(0);
        setIndex(0);
        setShowBack(false);
        setStats({ seen: 0, hard: 0, medium: 0, easy: 0, ai: 0 });
        // best-effort: sessão e contador não podem quebrar a geração
        try {
          const { startFlashcardSession } = await import("../services/flashcardSessionService");
          const sid = await startFlashcardSession(chosenFilters.subject, chosenFilters.topic, maxRequested);
          setSessionId(sid);
        } catch (sessionErr) {
          console.error("Falha ao iniciar sessão de flashcards", sessionErr);
        }
        try {
          await incrementFlashcardsUsed(Math.min(fc.length, maxRequested));
        } catch (incErr) {
          console.error("Falha ao atualizar contador diário de flashcards", incErr);
        }
      } catch (err) {
        console.error("Erro ao carregar flashcards", err);
        setError("Falha ao carregar flashcards.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [chosenFilters, filtersReady, requestedCount, limits.perGeneration, limits.remainingToday]);

  const current = cards[index];
  const seenCount = stats.seen;

  const showToastMsg = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const computeSubjectBias = () => {
    if (!chosenFilters.subject) return null;
    const subjectSessions = recentSessions.filter((s) => (s.subject || '') === chosenFilters.subject);
    if (!subjectSessions.length) return null;
    const totals = subjectSessions.reduce(
      (acc, s) => {
        acc.seen += s.totalSeen || 0;
        acc.easy += s.easy || 0;
        acc.medium += s.medium || 0;
        acc.hard += s.hard || 0;
        return acc;
      },
      { seen: 0, easy: 0, medium: 0, hard: 0 }
    );
    if (totals.seen < 5) return null;
    const accuracy = ((totals.easy + totals.medium) / totals.seen) * 100;
    if (accuracy >= 70) return 'hard';
    if (accuracy <= 50) return 'easy';
    return 'balanced';
  };

  const computeDifficultyBias = () => {
    const subjectBias = computeSubjectBias();
    if (subjectBias) return subjectBias;
    const totalAnswers = stats.hard + stats.medium + stats.easy;
    if (totalAnswers < 5) return 'balanced';
    const accuracy = stats.easy + stats.medium ? ((stats.easy + stats.medium) / totalAnswers) * 100 : 0;
    if (accuracy >= 70) return 'hard';
    if (accuracy <= 50) return 'easy';
    return 'balanced';
  };

  const loadMoreCards = async () => {
    if (loadingMore) return;
    // Não pre-carregar além do solicitado
    if (cards.length >= requestedCount) return;
    setLoadingMore(true);
    try {
      const remainingNeeded = Math.max(0, requestedCount - cards.length);
      const preloadCount = Math.max(1, remainingNeeded);
      const bias = computeDifficultyBias();
      const more = await getFlashcardsFromQuestions({ ...chosenFilters, limit: preloadCount, bias }, preloadCount);
      let uniquesCount = 0;
      setCards((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const uniques = more.filter((c) => !existingIds.has(c.id)).slice(0, remainingNeeded);
        uniquesCount = uniques.length;
        if (!uniques.length) return prev;
        return [...prev, ...uniques];
      });
      if (uniquesCount > 0) {
        setTotalAvailable((prev) => Math.max(prev || 0, cards.length + uniquesCount));
        setPreloaded((prev) => prev + uniquesCount);
        if (cards.length + uniquesCount >= requestedCount) {
          showToastMsg(`Concluiu seus ${requestedCount} cards!`);
        }
      }
    } catch (e) {
      console.error("Falha ao pré-carregar flashcards", e);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleNext = () => {
    const answered = showBack; // contou o verso?
    setShowBack(false);
    setAiAnswer("");

    if (answered) {
      setStats((s) => {
        const seen = Math.min(cards.length, Math.max(s.seen, index + 1));
        const nextStats = { ...s, seen };
        if (sessionId) {
          import("../services/flashcardSessionService").then(({ updateFlashcardSession }) => {
            updateFlashcardSession(sessionId, { totalSeen: seen });
          });
        }
        return nextStats;
      });
    }

    setIndex((prev) => {
      const next = Math.min(prev + 1, cards.length - 1);
      const remaining = cards.length - (next + 1);
      if (remaining <= 3 && cards.length < requestedCount) {
        loadMoreCards();
      }
      return next;
    });
  };

  const handlePrev = () => {
    setShowBack(false);
    setAiAnswer("");
    setIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSelectSubmit = () => {
    if (!chosenFilters.subject) {
      setError("Escolha uma matéria para começar.");
      return;
    }
    setFiltersReady(true);
  };

  const handleProfessor = async (card: Flashcard) => {
    setAiLoading(true);
    setAiAnswer("");
    try {
      const { getProfessorPobreExplanation } = await import("../services/aiService");
      // Usa um ID único por flashcard para evitar reaproveitar comentários cacheados de questões.
      const question = {
        id: `fc_${card.id || index}_${Date.now()}`,
        subject: chosenFilters.subject || card.subject || "Flashcards",
        topic: chosenFilters.topic || card.topic || "",
        text: card.front,
        options: [{ id: "A", text: card.back || "" }],
        correctOptionId: "A",
        comments: card.back,
      };
      const resp = await getProfessorPobreExplanation(question as any, "A", "flashcard");
      setAiAnswer(resp || "Sem resposta da IA.");
      if (sessionId) {
        import("../services/flashcardSessionService").then(async ({ updateFlashcardSession, updateUserFlashcardProgress }) => {
          const ai = stats.ai + 1;
          setStats((s) => ({ ...s, ai }));
          await updateFlashcardSession(sessionId, { aiOpened: ai, totalSeen: Math.max(stats.seen, index + 1) });
          await updateUserFlashcardProgress({ aiOpened: 1, seen: 0, subject: chosenFilters.subject, topic: chosenFilters.topic });
        });
      }
    } catch (err) {
      console.error(err);
      setAiAnswer("Erro ao consultar o Professor Pobre.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleDifficulty = (level: "hard" | "medium" | "easy") => {
    const nextStats = { ...stats };
    nextStats[level] += 1;
    nextStats.seen = Math.min(cards.length, Math.max(nextStats.seen, index + 1));
    setStats(nextStats);

    if (sessionId) {
      import("../services/flashcardSessionService").then(async ({ updateFlashcardSession, updateUserFlashcardProgress }) => {
        try {
          await updateFlashcardSession(sessionId, {
            hard: nextStats.hard,
            medium: nextStats.medium,
            easy: nextStats.easy,
            totalSeen: nextStats.seen,
            lastCardId: cards[index]?.id,
          });
          const hardInc = level === 'hard' ? 1 : 0;
          const medInc = level === 'medium' ? 1 : 0;
          const easyInc = level === 'easy' ? 1 : 0;
          await updateUserFlashcardProgress({
            hard: hardInc,
            medium: medInc,
            easy: easyInc,
            seen: 1,
            subject: chosenFilters.subject,
            topic: chosenFilters.topic,
          });
        } catch (e) {
          console.error("Falha ao salvar sessão de flashcards", e);
        }
      });
    }

    // Se está no último card, fica nele e mostra o resumo; senão, avança.
    if (index >= cards.length - 1) {
      setShowBack(true);
      return;
    }

    handleNext();
  };

  const showSummary = filtersReady && cards.length > 0 && index === cards.length - 1 && showBack;
  const totalInfo = totalAvailable !== null ? `${cards.length}/${totalAvailable} cards desta busca${loadingMore ? ' (carregando mais...)' : ''}` : `${cards.length} cards`;
  const accuracy = stats.seen > 0 ? Math.round(((stats.easy + stats.medium) / stats.seen) * 100) : null;

  const handleSaveExplanation = async (card: Flashcard) => {
    try {
      const explanation = aiAnswer || card.back || "";
      if (!explanation.trim()) {
        showToastMsg("Nada para salvar");
        return;
      }

      const payload = {
        id: "",
        questionId: card.id ? `fc_${card.id}` : undefined,
        subject: card.subject || chosenFilters.subject,
        topic: card.topic || chosenFilters.topic,
        statement: card.front,
        explanation,
        source: "flashcard" as const,
      };

      await saveExplanation(payload);
      setBookmarked((prev) => (card.id && !prev.includes(card.id) ? [...prev, card.id] : prev));
      showToastMsg("Explicação salva no Caderno.");
    } catch (err: any) {
      console.error("Erro ao salvar explicação", err);
      const msg = err?.message?.includes("logado") ? "Faça login para salvar no Caderno." : "Falha ao salvar explicação.";
      showToastMsg(msg);
    }
  };

  const SummaryBlock = () => {
    if (!stats.seen) return null;
    const total = stats.seen;
    return (
      <div className="mt-6 bg-white rounded-3xl shadow-xl border border-gray-100 p-6 space-y-3">
        <h3 className="font-black text-gray-900 text-xl">Resumo dos Flashcards</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-3">
            <p className="text-xs text-emerald-700 font-semibold uppercase tracking-[0.12em]">Vistos</p>
            <p className="text-2xl font-black text-emerald-800">{total}/{cards.length}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3">
            <p className="text-xs text-amber-700 font-semibold uppercase tracking-[0.12em]">Difícil</p>
            <p className="text-2xl font-black text-amber-800">{stats.hard}</p>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3">
            <p className="text-xs text-blue-700 font-semibold uppercase tracking-[0.12em]">Médio</p>
            <p className="text-2xl font-black text-blue-800">{stats.medium}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-3">
            <p className="text-xs text-emerald-700 font-semibold uppercase tracking-[0.12em]">Fácil</p>
            <p className="text-2xl font-black text-emerald-800">{stats.easy}</p>
          </div>
          <div className="bg-purple-50 border border-purple-100 rounded-2xl p-3">
            <p className="text-xs text-purple-700 font-semibold uppercase tracking-[0.12em]">Professor</p>
            <p className="text-2xl font-black text-purple-800">{stats.ai}</p>
          </div>
          {accuracy !== null && (
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-3">
              <p className="text-xs text-gray-600 font-semibold uppercase tracking-[0.12em]">Acerto (estimado)</p>
              <p className="text-2xl font-black text-gray-800">{accuracy}%</p>
            </div>
          )}
        </div>
        {bookmarked.length > 0 && (
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 text-sm">
            <p className="text-xs text-amber-700 font-semibold uppercase tracking-[0.12em]">Marcados para revisar</p>
            <p className="text-base font-bold text-amber-800">{bookmarked.length} card(s)</p>
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => {
              setFiltersReady(false);
              setCards([]);
              setStats({ seen: 0, hard: 0, medium: 0, easy: 0, ai: 0 });
              setIndex(0);
              setAiAnswer("");
              setShowBack(false);
            }}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-4 py-3 rounded-xl shadow-lg shadow-emerald-500/20"
          >
            Gerar mais cards
          </button>
          <button
            onClick={onBack}
            className="bg-white text-gray-700 font-semibold px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-50"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  };

  if (!filtersReady) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="max-w-screen-2xl mx-auto px-6 pt-8 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs font-bold text-emerald-600 uppercase tracking-[0.12em]">Modo Flashcards</p>
            <h1 className="text-2xl font-black text-gray-900">Escolha o que quer revisar</h1>
            <p className="text-sm text-gray-500">Selecione uma matéria (e tópico opcional) antes de gerar os cards.</p>
          </div>
          <button
            onClick={onBack}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full font-bold shadow-sm hover:bg-emerald-50 transition"
          >
            ← Voltar
          </button>
        </header>

        <main className="max-w-screen-xl xl:max-w-screen-2xl mx-auto px-6 py-10 space-y-6">
          <div className="bg-white/95 rounded-3xl shadow-md border border-gray-100 p-6 space-y-5">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Selecione a matéria</span>
              <span className="font-semibold text-gray-700">{totalInfo}</span>
            </div>
            <div>
              {recentSessions.length > 0 && (
                <div className="mb-4 bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-[0.1em] mb-2">Desempenho em flashcards</p>
                  <div className="space-y-2 text-sm text-emerald-900">
                    {recentSessions.map((s) => {
                      const seen = s.totalSeen ?? 0;
                      const acc = seen > 0 ? Math.round(((s.easy || 0) + (s.medium || 0)) / seen * 100) : null;
                      return (
                        <div key={s.id} className="flex items-center justify-between gap-2 text-xs">
                          <div className="flex flex-col">
                            <span className="font-bold text-gray-800">{s.subject || 'Geral'} {s.topic ? `• ${s.topic}` : ''}</span>
                            <span className="text-gray-500">Vistos: {seen} / Pedidos: {s.totalRequested ?? '?'} • IA: {s.aiOpened ?? 0} {acc !== null ? `• Acerto: ${acc}%` : ''}</span>
                          </div>
                          <span className="text-[11px] text-gray-500">{s.startedAt?.toDate ? new Date(s.startedAt.toDate()).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                  {resumeSession && (
                    <div className="mt-3 flex items-center justify-between text-xs text-gray-700">
                      <span>Continuar de onde parei ({resumeSession.subject || 'Geral'}{resumeSession.topic ? ` • ${resumeSession.topic}` : ''})</span>
                      <button
                        onClick={() => {
                          const remaining = Math.max(5, (resumeSession.totalRequested || 20) - (resumeSession.totalSeen || 0));
                          setChosenFilters({
                            subject: resumeSession.subject || undefined,
                            topic: resumeSession.topic || undefined,
                          });
                          setRequestedCount(remaining);
                          setFiltersReady(true);
                        }}
                        className="px-3 py-1.5 bg-emerald-500 text-white font-bold rounded-full hover:bg-emerald-600 transition"
                      >
                        Continuar
                      </button>
                    </div>
                  )}
                </div>
              )}
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-[0.12em] mb-2">Matéria</label>
              <select
                value={chosenFilters.subject || ""}
                onChange={(e) => {
                  const subject = e.target.value || undefined;
                  setChosenFilters((prev) => ({ ...prev, subject, topic: undefined }));
                }}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
              >
                <option value="">Selecione...</option>
                {availableFilters.subjects.filter((s) => s && s !== "Todas").map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {chosenFilters.subject && (
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-[0.12em] mb-2">Tópico (opcional)</label>
                <select
                  value={chosenFilters.topic || ""}
                  onChange={(e) => {
                    const topic = e.target.value || undefined;
                    setChosenFilters((prev) => ({ ...prev, topic }));
                  }}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                >
                  <option value="">Todos os tópicos</option>
                  {(availableFilters.topics[chosenFilters.subject] || []).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-[0.12em] mb-2">
                Quantos cards? (máx. {limits.perGeneration}
                {!limits.isPro ? ` | Free: ${limits.dailyLimit}/dia, ${limits.remainingToday} restantes` : ''})
              </label>
              <input
                type="number"
                min={5}
                max={Math.min(limits.perGeneration, Math.max(5, limits.remainingToday || limits.perGeneration))}
                step={5}
                value={requestedCount}
                onChange={(e) => setRequestedCount(Math.max(5, Math.min(Math.min(limits.perGeneration, limits.remainingToday || limits.perGeneration), parseInt(e.target.value) || 5)))}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
              />
              {!limits.isPro && (
                <p className="text-xs text-amber-600 mt-1">Plano grátis: limite diário de {limits.dailyLimit} cards. Restantes hoje: {limits.remainingToday}.</p>
              )}
            </div>

            {error && <p className="text-red-600 text-sm font-semibold">{error}</p>}

            <button
              onClick={handleSelectSubmit}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl shadow-lg shadow-emerald-200"
            >
              Gerar flashcards
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-emerald-50 flex items-center justify-center px-6">
        <div className="bg-white rounded-3xl shadow-xl p-6 max-w-md w-full text-center space-y-4">
          <p className="text-red-600 font-semibold">{error}</p>
          <button
            onClick={onBack}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="min-h-screen bg-emerald-50 flex items-center justify-center px-6">
        <div className="bg-white rounded-3xl shadow-xl p-6 max-w-md w-full text-center space-y-4">
          <p className="text-gray-700 font-semibold">Nenhum flashcard disponível para esses filtros.</p>
          <button
            onClick={onBack}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="max-w-screen-2xl mx-auto px-6 pt-8 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-bold text-emerald-600 uppercase tracking-[0.12em]">Modo Flashcards</p>
          <h1 className="text-2xl font-black text-gray-900">Estudar por Flashcards</h1>
          <p className="text-sm text-gray-500">{activeFilterLabel}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
            {index + 1} / {cards.length}
          </div>
          <button
            onClick={onBack}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full font-bold shadow-sm hover:bg-emerald-50 transition"
          >
            ← Voltar
          </button>
        </div>
      </header>

      <main className="max-w-screen-xl xl:max-w-screen-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="bg-white/95 rounded-3xl shadow-md border border-gray-100 p-4 sm:p-6 lg:p-8 min-h-[380px] flex flex-col gap-4">
          <div className="flex items-center justify-between text-sm text-gray-500 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              <span className="font-semibold">{current.subject || "Geral"}</span>
              {current.topic && <span className="text-gray-400">• {current.topic}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                disabled={index === 0}
                className={`px-3 py-1.5 rounded-full border text-sm font-semibold transition ${index === 0 ? "text-gray-300 border-gray-100 cursor-not-allowed" : "text-gray-700 border-gray-200 hover:bg-gray-50"}`}
              >
                ← Anterior
              </button>
              <button
                onClick={handleNext}
                disabled={index === cards.length - 1}
                className={`px-3 py-1.5 rounded-full border text-sm font-semibold transition ${index === cards.length - 1 ? "text-gray-300 border-gray-100 cursor-not-allowed" : "text-gray-700 border-gray-200 hover:bg-gray-50"}`}
              >
                Próximo →
              </button>
            </div>
          </div>

          <div className="flex-1 bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-2xl p-4 sm:p-6 shadow-inner">
            <h3 className="text-xs font-bold text-emerald-700 uppercase tracking-[0.15em] mb-2">Frente</h3>
            <p className="text-gray-800 leading-relaxed whitespace-pre-line text-base sm:text-lg">{current.front}</p>

            {showBack && (
              <div className="mt-6 pt-4 border-t border-emerald-100">
                <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
                  <span className="font-semibold text-emerald-700">Verso</span>
                  <span className="font-bold text-gray-600">Card {index + 1} / {requestedCount}</span>
                </div>
                <p className="text-gray-800 leading-relaxed whitespace-pre-line text-base sm:text-lg">{current.back}</p>
                {aiAnswer && (
                  <div className="mt-4 p-4 border border-emerald-200 rounded-2xl bg-emerald-50 text-sm text-gray-800 shadow-sm">
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                      <p className="font-bold text-emerald-700">Professor Pobre</p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSaveExplanation(current)}
                          className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shadow"
                        >
                          💾 Salvar Explicação
                        </button>
                        <button
                          onClick={() => onViewSavedExplanations?.()}
                          className="px-3 py-1.5 rounded-lg bg-white border border-emerald-200 hover:bg-emerald-100 text-emerald-700 text-xs font-bold"
                        >
                          📒 Ver Caderno
                        </button>
                      </div>
                    </div>
                    <ReactMarkdown
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      components={{
                        p: (props) => <p className="mb-2 leading-relaxed" {...props} />,
                        strong: (props) => <strong className="font-semibold" {...props} />,
                        em: (props) => <em className="italic" {...props} />,
                        ul: (props) => <ul className="list-disc pl-5 mb-2" {...props} />,
                        ol: (props) => <ol className="list-decimal pl-5 mb-2" {...props} />,
                        li: (props) => <li className="mb-1" {...props} />,
                        br: () => <br />,
                      }}
                    >
                      {aiAnswer}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!showBack ? (
              <button
                onClick={() => setShowBack(true)}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl shadow-lg shadow-emerald-200"
              >
                Mostrar Resposta
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleProfessor(current)}
                  className="w-full bg-white border-2 border-emerald-100 text-emerald-700 font-bold py-3 rounded-2xl transition hover:bg-emerald-50 flex items-center justify-center gap-2"
                  disabled={aiLoading}
                >
                  {aiLoading ? "Consultando..." : "Professor Pobre"}
                </button>
                <div className="grid grid-cols-3 gap-3 sm:col-span-1">
                  <button onClick={() => handleDifficulty("hard") } className="bg-amber-50 text-amber-800 font-bold py-3 rounded-2xl border border-amber-100 hover:bg-amber-100">Difícil</button>
                  <button onClick={() => handleDifficulty("medium")} className="bg-blue-50 text-blue-800 font-bold py-3 rounded-2xl border border-blue-100 hover:bg-blue-100">Médio</button>
                  <button onClick={() => handleDifficulty("easy")} className="bg-emerald-50 text-emerald-700 font-bold py-3 rounded-2xl border border-emerald-100 hover:bg-emerald-100">Fácil</button>
                </div>
              </>
            )}
          </div>
        </div>

        {showSummary && (
          <div className="mt-6 bg-white/95 rounded-3xl shadow-md border border-gray-100 p-6 space-y-3">
            <h3 className="font-black text-gray-900 text-xl">Resumo dos Flashcards</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-3">
                <p className="text-xs text-emerald-700 font-semibold uppercase tracking-[0.12em]">Vistos</p>
                <p className="text-2xl font-black text-emerald-800">{stats.seen}/{cards.length}</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3">
                <p className="text-xs text-amber-700 font-semibold uppercase tracking-[0.12em]">Difícil</p>
                <p className="text-2xl font-black text-amber-800">{stats.hard}</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3">
                <p className="text-xs text-blue-700 font-semibold uppercase tracking-[0.12em]">Médio</p>
                <p className="text-2xl font-black text-blue-800">{stats.medium}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-3">
                <p className="text-xs text-emerald-700 font-semibold uppercase tracking-[0.12em]">Fácil</p>
                <p className="text-2xl font-black text-emerald-800">{stats.easy}</p>
              </div>
              <div className="bg-purple-50 border border-purple-100 rounded-2xl p-3">
                <p className="text-xs text-purple-700 font-semibold uppercase tracking-[0.12em]">Professor</p>
                <p className="text-2xl font-black text-purple-800">{stats.ai}</p>
              </div>
              {accuracy !== null && (
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-3">
                  <p className="text-xs text-gray-600 font-semibold uppercase tracking-[0.12em]">Acerto (estimado)</p>
                  <p className="text-2xl font-black text-gray-800">{accuracy}%</p>
                </div>
              )}
            </div>
            {bookmarked.length > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 text-sm">
                <p className="text-xs text-amber-700 font-semibold uppercase tracking-[0.12em]">Marcados para revisar</p>
                <p className="text-base font-bold text-amber-800">{bookmarked.length} card(s)</p>
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => {
                  setFiltersReady(false);
                  setStats({ seen: 0, hard: 0, medium: 0, easy: 0, ai: 0 });
                  setIndex(0);
                  setCards([]);
                  setAiAnswer("");
                }}
                className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-2xl"
              >
                Gerar novos cards
              </button>
              <button
                onClick={onBack}
                className="px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-2xl"
              >
                Voltar ao Dashboard
              </button>
            </div>
          </div>
        )}
      </main>
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 bg-emerald-600 text-white px-4 py-2 rounded-xl shadow-lg text-sm">
          {toast}
        </div>
      )}
    </div>
  );
};

export default FlashcardsPage;
